<script setup lang="ts">
  import { TUNDR_ICONS } from '@tundr/theme-assets/tundr-icons/tundr-icons-icons';
  import { BASE_CURRENCY_DIVISION } from '@tundr/i18n';
  import { computed, watchEffect } from 'vue';
  import {
    CurrencyDisplay,
    type CurrencyInputOptions,
    useCurrencyInput,
    ValueScaling,
  } from 'vue-currency-input';
  import FontIcon from '../../FontIcon/FontIcon.vue';
  import TextInput from '../TextInput/TextInput.vue';
  import type {
    CurrencyInputEmits,
    CurrencyInputProps,
  } from './CurrencyInput.types';

  const props = withDefaults(defineProps<CurrencyInputProps>(), {
    placeholder: '0,00',
    disabled: false,
    required: false,
    announceWarning: true,
  });

  const emit = defineEmits<CurrencyInputEmits>();

  const currencyOptions = computed<CurrencyInputOptions>(() => ({
    currency: 'EUR',
    locale: 'it-IT',
    precision: 2,
    currencyDisplay: CurrencyDisplay.hidden,
    hideCurrencySymbolOnFocus: false,
    hideGroupingSeparatorOnFocus: false,
    useGrouping: true,
    valueScaling: ValueScaling.precision,
    valueRange: {
      min: props.min,
      max: props.max,
    },
  }));

  const { inputRef, setValue, setOptions, numberValue, formattedValue } =
    useCurrencyInput(currencyOptions.value);

  void inputRef;

  watchEffect(() => {
    setValue(props.modelValue);
  });

  watchEffect(() => {
    setOptions(currencyOptions.value);
  });

  const clamp = (val: number, min?: number, max?: number) => {
    if (min === undefined && max === undefined) {
      return val;
    }
    if (min === undefined) {
      return Math.min(val, max! * BASE_CURRENCY_DIVISION);
    }
    if (max === undefined) {
      return Math.max(val, min * BASE_CURRENCY_DIVISION);
    }
    return Math.min(
      Math.max(val, min * BASE_CURRENCY_DIVISION),
      max * BASE_CURRENCY_DIVISION,
    );
  };

  const onBlur = () => {
    const value = clamp(numberValue.value ?? 0, props.min, props.max);
    emit('blur', value, numberValue.value ?? 0);
  };
</script>

<template>
  <TextInput
    :id="id"
    ref="inputRef"
    :label="label"
    :helper-text="helperText"
    :error="error"
    :warning="warning"
    :announce-warning="announceWarning"
    :name="name"
    :model-value="formattedValue"
    :placeholder="placeholder"
    :disabled="disabled"
    :disabled-reason="disabledReason"
    :required="required"
    :size="size"
    type="text"
    input-mode="decimal"
    @blur="onBlur"
  >
    <template v-if="$slots.extraLabel" #extraLabel>
      <slot name="extraLabel" />
    </template>
    <template #icon>
      <FontIcon :icon-name="TUNDR_ICONS.EURO" />
    </template>
  </TextInput>
</template>
